import { Col, Row, Spinner2, Text } from 'components';
import { Api, QUIZ_ROLE, getQuizRole, theme } from 'lib';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { FiChevronDown } from 'react-icons/fi';

const SelectContainer = styled.div`
  position: relative;
  width: 100%;
  height: 44px;
  border-radius: 8px;
  border: 1px solid ${theme.GREY_30};
  background: white;
  display: flex;
  align-items: center;
  padding: 0 14px;
`;

const StyledSelect = styled.select`
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  font-family: CeraPro;
  font-size: 14px;
  cursor: pointer;
  appearance: none;
  padding-right: 20px;
  color: ${(props) => (props.value ? theme.GREY_80 : theme.GREY_50)};

  &:focus {
    outline: none;
  }
`;

const SelectArrow = styled.div`
  position: absolute;
  right: 10px;
  pointer-events: none;
  color: ${theme.GREY_60};
  display: flex;
  align-items: center;
`;

export const getGameLabel = (game, teamId) => {
  if (!game) return '';
  const isHome = game.home?._id === teamId;
  const opponent = isHome ? game.away : game.home;
  // Sports-API games may not link both sides to platform users — fall back to the name string.
  const opponentName = opponent?.name || (isHome ? game.awayName : game.homeName) || 'TBD';
  const prefix = isHome ? 'vs' : '@';
  // Kick-off TIME as well as the date. Day precision alone made two fixtures against the same
  // opponent on the same day byte-identical in the dropdown — which is exactly what a deleted mock
  // game and its replacement look like, so an admin picking "the right one" could not tell they had
  // selected an id that no longer existed. The quiz then saved cleanly and never appeared.
  const dateStr = game.date ? format(new Date(game.date), 'MMM dd, yyyy HH:mm') : '';
  return `${dateStr} — ${prefix} ${opponentName}`;
};

/**
 * `refreshKey` — change it to force a refetch of the game list.
 *
 * The forms that host this picker STAY MOUNTED so their side-panel transition can run on close as
 * well as open, and this effect keys only on the team. So the list was fetched once per page load
 * and never again: delete a fixture, recreate it, reopen the form, and the dropdown still offered
 * the deleted game's `_id`. Pass the panel's `visible` flag (or any value that changes when the form
 * reopens) and the list is re-read each time it matters.
 *
 * Callers that mount and unmount normally can ignore it.
 */
const GamePicker = ({ teamId, value, onChange, label, required, disabled, refreshKey }) => {
  const user = useSelector((state) => state.user);
  const isP4P = getQuizRole(user) === QUIZ_ROLE.P4P;

  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // P4P admins use /admin/games/get (all of the channel's games). Team (channel-owner)
  // admins can't hit that endpoint (adminOnly → 403), so they reuse the existing owner-scoped
  // /admin/polls/upcomingGames, which returns only their upcoming fixtures (same as the poll
  // game picker — it's just "this channel's upcoming games", not poll-specific).
  useEffect(() => {
    if (!teamId || disabled) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      let list = [];
      if (isP4P) {
        const res = await Api.post(
          '/admin/games/get',
          { teamId, sortBy: { date: -1 }, limit: 100 },
          null,
          { silent: true }
        );
        if (res?.games) list = res.games;
      } else {
        const res = await Api.get(`/admin/polls/upcomingGames?teamId=${teamId}&limit=50`, null, {
          silent: true
        });
        if (res?.data) list = res.data;
      }
      if (!cancelled) {
        setGames(list);
        setLoading(false);
        setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, disabled, isP4P, refreshKey]);

  /**
   * A selection that is not in the list any more is DROPPED, and said out loud.
   *
   * Belt and braces behind the refetch above, and the part that actually prevents the bad write: a
   * <select> whose `value` matches no <option> silently displays the placeholder, so the form looked
   * like nothing was chosen while state still held a stale id — and `required` passed, because the id
   * was truthy. Submitting it pinned the quiz to a game that no longer existed, which the backend
   * accepted and no surface could ever resolve.
   *
   * Skipped while `disabled` (edit mode never fetches, so `games` is legitimately empty) and until
   * the first fetch resolves, so an in-flight load does not clear a valid selection.
   */
  const staleSelection =
    !disabled && loaded && !!value && !games.some((g) => g._id === value);

  useEffect(() => {
    if (staleSelection) onChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staleSelection]);

  return (
    <Col wid="100%">
      {label && (
        <Text h4 marg="0 0 8px">
          {label}
          {required && ' *'}
        </Text>
      )}
      {loading ? (
        <Row center pad="8px 0">
          <Spinner2 />
          <Text marg="0 0 0 8px" col={theme.GREY_60}>
            Loading games...
          </Text>
        </Row>
      ) : (
        <SelectContainer style={disabled ? { opacity: 0.5 } : undefined}>
          <StyledSelect
            value={value || ''}
            onChange={(e) => onChange(e.target.value || null)}
            disabled={disabled}
          >
            <option value="">{required ? 'Select a game...' : 'No game (optional)'}</option>
            {games.map((g) => (
              <option key={g._id} value={g._id}>
                {getGameLabel(g, teamId)}
              </option>
            ))}
          </StyledSelect>
          <SelectArrow>
            <FiChevronDown size={14} />
          </SelectArrow>
        </SelectContainer>
      )}
    </Col>
  );
};

export default GamePicker;
